import { useEffect, useRef } from "react";
import { MapPin, Navigation, ExternalLink } from "lucide-react";
import { MapContainer, TileLayer, Circle, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/**
 * MapLocation — Airbnb-style map widget.
 * • Rounded card container with a muted OSM tile layer
 * • Custom lime + forest SVG pin
 * • Semi-transparent forest-green radius circle (~450 m) — approximate
 *   location, in Airbnb's "protect exact address" convention
 * • Distance chips + Open-in-Google-Maps CTA below the map
 */

const COORDS = [18.5344, 73.3186];
const RADIUS_METERS = 450;
const GOOGLE_MAPS_URL = "https://maps.app.goo.gl/KegRLMsksSSpz5rr9";
const DIRECTIONS_URL = `https://www.google.com/maps/dir/?api=1&destination=${COORDS[0]},${COORDS[1]}`;

// SVG-based pin — no default Leaflet asset dependencies (which 404 on webpack builds)
const PIN_HTML = `
<div style="position: relative; width: 44px; height: 56px; transform: translate(-50%, -100%);">
  <svg width="44" height="56" viewBox="0 0 44 56" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22 2 C 10 2, 2 11, 2 22 C 2 36, 22 54, 22 54 C 22 54, 42 36, 42 22 C 42 11, 34 2, 22 2 Z"
      fill="#0B4A3D" stroke="#F4F4EF" stroke-width="2"/>
    <circle cx="22" cy="22" r="8" fill="#A6CE39" stroke="#0B4A3D" stroke-width="2"/>
    <circle cx="22" cy="22" r="3" fill="#0B4A3D"/>
  </svg>
</div>`;

const customIcon = L.divIcon({
  html: PIN_HTML,
  className: "phc-map-pin",
  iconSize: [44, 56],
  iconAnchor: [22, 56],
});

const DISTANCE_CHIPS = [
  { label: "Mumbai", value: "2 hours" },
  { label: "Pune", value: "2 hours" },
  { label: "Expressway", value: "40 mins" },
  { label: "Goa Highway", value: "15 mins" },
];

export default function MapLocation() {
  const mapRef = useRef(null);

  // Force Leaflet to invalidate size on mount — fixes half-rendered tiles
  useEffect(() => {
    const t = setTimeout(() => {
      if (mapRef.current) mapRef.current.invalidateSize();
    }, 400);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="section-pad bg-offwhite" data-testid="home-map-section">
      <div className="container-editorial px-5 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 mb-10">
          <aside className="md:col-span-4">
            <div className="eyebrow text-teal mb-4">
              <span className="editorial-rule" /> The Location
            </div>
            <h2 className="font-display text-forest text-4xl md:text-5xl leading-tight">
              A ridge<br />
              you can<br />
              actually find.
            </h2>
            <p className="mt-6 text-forest/70 leading-relaxed max-w-md">
              Pali Hill City sits in Sudhagad taluka, Raigad — a 300-acre
              private hill within the shaded circle below. The exact plot map
              is shared during your consultation or site visit.
            </p>
          </aside>

          <div className="md:col-span-8">
            <div className="bg-white rounded-2xl overflow-hidden border border-forest/10 shadow-[0_30px_60px_-30px_rgba(11,74,61,0.35)]">
              {/* Map */}
              <div className="relative h-[380px] md:h-[440px] w-full">
                <MapContainer
                  center={COORDS}
                  zoom={13}
                  scrollWheelZoom={false}
                  ref={(m) => (mapRef.current = m)}
                  style={{ height: "100%", width: "100%" }}
                  attributionControl={false}
                  className="phc-map"
                >
                  <TileLayer
                    url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                    subdomains={["a", "b", "c", "d"]}
                  />
                  <Circle
                    center={COORDS}
                    radius={RADIUS_METERS}
                    pathOptions={{
                      color: "#0B4A3D",
                      weight: 2,
                      opacity: 0.5,
                      fillColor: "#A6CE39",
                      fillOpacity: 0.22,
                    }}
                  />
                  <Marker position={COORDS} icon={customIcon}>
                    <Popup>
                      <div style={{ fontFamily: "Manrope, sans-serif" }}>
                        <div style={{ fontWeight: 600, color: "#0B4A3D" }}>Pali Hill City</div>
                        <div style={{ fontSize: 12, color: "#4A5A54" }}>Bhavshet, Sudhagad · Raigad</div>
                      </div>
                    </Popup>
                  </Marker>
                </MapContainer>

                {/* Airbnb-style floating info card top-left */}
                <div className="absolute top-4 left-4 z-[400] bg-white/95 backdrop-blur-sm border border-forest/15 rounded-xl px-4 py-3 shadow-lg pointer-events-none">
                  <div className="flex items-center gap-2 text-forest">
                    <MapPin size={14} className="text-teal" />
                    <span className="eyebrow text-teal">Approx. location</span>
                  </div>
                  <div className="font-display text-forest text-base mt-1 leading-tight">
                    Bhavshet, Sudhagad
                  </div>
                  <div className="text-forest/60 text-[11px] mt-0.5">Raigad, Maharashtra · 410205</div>
                </div>

                {/* Attribution (tiny, bottom-right) */}
                <div className="absolute bottom-2 right-2 z-[400] text-[9px] text-forest/40 bg-white/80 px-2 py-0.5 rounded pointer-events-none tracking-wide">
                  © OpenStreetMap · CartoDB
                </div>
              </div>

              {/* Footer strip: distances + CTAs */}
              <div className="border-t border-forest/10 p-5 md:p-6 space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {DISTANCE_CHIPS.map((c) => (
                    <div key={c.label} className="border-l-2 border-lime pl-3">
                      <div className="font-display text-forest text-xl leading-none">{c.value}</div>
                      <div className="eyebrow text-forest/60 mt-1.5">from {c.label}</div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-3 pt-1 border-t border-forest/10">
                  <a
                    data-testid="map-directions"
                    href={DIRECTIONS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary text-sm py-2.5"
                  >
                    <Navigation size={15} /> Get Directions
                  </a>
                  <a
                    data-testid="map-open-google"
                    href={GOOGLE_MAPS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-outline-forest text-sm py-2.5"
                  >
                    Open in Google Maps
                    <ExternalLink size={13} />
                  </a>
                </div>
                <p className="text-[10px] text-forest/50 leading-relaxed">
                  Pin is approximate for privacy. The exact plot layout is shared
                  in your site visit or online consultation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
